import { useState } from 'react';
import common from '../styles/common.module.css';
import MapFolderView from './MapFolderView';
import BattlefieldFileButton from './BattlefieldFileButton';
import styles from '../styles/Battlefield.module.css';
import { parseAsciiMap, legendHelp } from '../engine/battlefield/asciiMap';
import { BATTLEFIELD_PRESETS, buildPreset } from '../engine/battlefield/presets';
import { isNewer, staleCopies } from '../engine/assets/provenance';
import LibraryPicker from './assets/LibraryPicker';

/**
 * How a saved map is told apart from a preset in one <select>.
 *
 * Only the SAVED side is prefixed. A preset's option value stays its
 * bare key, because that is the contract every caller and every spec
 * already speaks - `selectOption('courtyard')` should not have to become
 * `selectOption('preset:courtyard')` for a list that merely grew.
 */
const SAVED = 'saved:';

/** A <select> that acts on a choice and snaps back to its placeholder. */
function ActionSelect({ label, testId, placeholder, onChoose, children }) {
  return (
    <select
      className={styles.picker}
      aria-label={label}
      data-testid={testId}
      value=""
      onChange={(e) => e.target.value && onChoose(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {children}
    </select>
  );
}

/** One option per saved map, valued by name (or by `prefix` + name); a copy the library has moved past says so. */
function libraryOptions(library, prefix = '', rows = []) {
  return library.map((entry) => (
    <option key={entry.name} value={`${prefix}${entry.name}`}>
      {entry.name}
      {isNewer(rows, entry.origin) ? ' · newer in your library' : ''}
    </option>
  ));
}

/** The presets, then this session's maps. */
function LoadMap({ library, onPick, rows }) {
  return (
    <ActionSelect label="Load a battlefield" testId="battlefield-preset" placeholder="Load map…" onChoose={onPick}>
      <optgroup label="Presets">
        {BATTLEFIELD_PRESETS.map((p) => (
          <option key={p.key} value={p.key}>
            {p.label}
          </option>
        ))}
      </optgroup>
      {library.length > 0 && <optgroup label="This session">{libraryOptions(library, SAVED, rows)}</optgroup>}
    </ActionSelect>
  );
}

function SaveMap({ name, setName, onSave }) {
  return (
    <>
      <label className={styles.buildField}>
        <input
          className={common.input}
          aria-label="Map name"
          value={name}
          placeholder="Save as…"
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <button
        type="button"
        className={common.btnSmall}
        data-testid="build-save"
        disabled={!name.trim()}
        onClick={() => {
          onSave?.(name.trim());
          setName('');
        }}
      >
        Save
      </button>
    </>
  );
}

/**
 * Delete and export. The library lives in the session and dies with it;
 * files are how a map outlives one - the same answer characters have
 * always had. One map or the whole shelf; same format.
 */
function LibraryShelf({ library, onDeleteSaved, onExportMaps }) {
  if (library.length === 0) return null;
  return (
    <>
      <ActionSelect label="Delete a saved battlefield" placeholder="Delete…" onChoose={(v) => onDeleteSaved?.(v)}>
        {libraryOptions(library)}
      </ActionSelect>
      <ActionSelect
        label="Export a map to a file"
        testId="build-export"
        placeholder="Export…"
        onChoose={(v) => onExportMaps?.(v === '*' ? null : v)}
      >
        <option value="*">The whole library ({library.length})</option>
        {libraryOptions(library)}
      </ActionSelect>
    </>
  );
}

/** One file of maps - how you take a map somebody sent you. */
function ImportFile({ onImportMaps }) {
  return (
    <BattlefieldFileButton
      className={common.btnSmall}
      testId="build-import-file"
      accept="application/json,.json"
      inputLabel="Import maps from a file"
      onFile={onImportMaps}
    >
      Import…
    </BattlefieldFileButton>
  );
}

/** A disclosure button: Folder…, Type a map…. */
function PaneToggle({ testId, open, setOpen, children }) {
  return (
    <button
      type="button"
      className={common.btnSmall}
      data-testid={testId}
      aria-expanded={open}
      onClick={() => setOpen((s) => !s)}
    >
      {children}
    </button>
  );
}

/**
 * Type a map. IT REPORTS AND BUILDS, it does not choose between them:
 * unknown characters are not fatal - the rest of the map still lands, and
 * the pane stays open to say which ones it did not know.
 */
function AsciiPane({ ascii, boundary, onImportAscii }) {
  const { text, setText, unknown, setUnknown } = ascii;
  const build = () => {
    const result = parseAsciiMap(text, { tileSize: boundary?.tileSize, name: 'Typed map' });
    setUnknown(result.unknown);
    onImportAscii?.(result.terrain);
  };
  return (
    <div className={styles.importPane} data-testid="ascii-import">
      <textarea
        className={common.input}
        aria-label="Map text"
        rows={8}
        value={text}
        placeholder={legendHelp()}
        onChange={(e) => setText(e.target.value)}
      />
      <div className={styles.buildRow}>
        <button
          type="button"
          className={common.btnSmall}
          data-testid="ascii-apply"
          disabled={!text.trim()}
          onClick={build}
        >
          Build it
        </button>
        {unknown.length > 0 && (
          <span className={styles.importWarn} data-testid="ascii-unknown">
            Unknown: {unknown.join(' ')}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * The session's own shelf tools: save, delete, export, one file, a folder.
 * A WHOLE FOLDER comes in the way characters always have; `Import…` beside
 * it takes one file and stays - it is how you take a map somebody sent
 * you. No "This session" tab: the load picker already IS that list.
 */
function SessionShelf({ p, ui }) {
  return (
    <>
      <SaveMap name={ui.name} setName={ui.setName} onSave={p.onSave} />
      <LibraryShelf library={p.library} onDeleteSaved={p.onDeleteSaved} onExportMaps={p.onExportMaps} />
      <ImportFile onImportMaps={p.onImportMaps} />
      <PaneToggle testId="build-import-folder" open={ui.showFolder} setOpen={ui.setShowFolder}>
        Folder…
      </PaneToggle>
    </>
  );
}

/**
 * Everything the admin remembers - held here, not in the pieces, so a
 * half-typed name or map survives leaving Build and coming back.
 */
function useMapAdminState() {
  const [name, setName] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [showFolder, setShowFolder] = useState(false);
  const [text, setText] = useState('');
  const [unknown, setUnknown] = useState([]);
  return {
    name,
    setName,
    showImport,
    setShowImport,
    showFolder,
    setShowFolder,
    ascii: { text, setText, unknown, setUnknown },
  };
}

/** The authoring half, shown only while building. */
function Authoring({ p, ui }) {
  const sessionLibrary = p.sessionLibrary ?? true;
  return (
    <>
      {sessionLibrary && <SessionShelf p={p} ui={ui} />}
      <PaneToggle testId="build-import" open={ui.showImport} setOpen={ui.setShowImport}>
        Type a map…
      </PaneToggle>
      {sessionLibrary && ui.showFolder && (
        <div className={styles.importPane} data-testid="map-folder-pane">
          <MapFolderView
            files={p.mapFolderFiles}
            onFiles={p.onMapFolderFiles}
            onImport={(maps) => p.onImportMapEntries?.(maps)}
          />
        </div>
      )}
      {ui.showImport && <AsciiPane ascii={ui.ascii} boundary={p.boundary} onImportAscii={p.onImportAscii} />}
    </>
  );
}

/**
 * BattlefieldMapAdmin - one shelf, and the ways on and off it.
 *
 * THERE WERE TWO LOADERS IN TWO PLACES. `Load map…` in the toolbar was
 * hardcoded to the three presets; saved AND imported maps lived in
 * `Load saved…`, buried in the build drawer, which only rendered when
 * the library was non-empty - so in Play mode you could only ever load a
 * preset, and an imported map appeared to have vanished (v8.0.0).
 *
 * The library is the presets PLUS whatever this session has loaded. The
 * presets are grouped and cannot be deleted, because they are rebuilt
 * from code rather than stored; everything saved or imported joins them
 * underneath.
 *
 * `sessionLibrary` - is there a SESSION behind this panel? The Workshop's
 * map editor renders the battlefield bare, so Save as…, Export…, Import…
 * and Folder… had nothing wired to them and did nothing at all when
 * clicked (v8.87.0). Presets and Type a map… stay, because those only
 * need `setTerrain`, which the workshop does pass.
 */
/**
 * The GM's asset library at the table (v11.71.0): "From my library…" copies
 * maps into this session - several at once (the user, 2026-10-20) - and loads
 * one when one is picked (the table holds one map); "Update from my library…" lists the
 * copies the library has moved past. Only for a GM whose account can host.
 */
function FromLibrary({ tableAssets, library }) {
  const [open, setOpen] = useState(false);
  const stale = staleCopies(library, tableAssets.lib.assets);
  const update = (name) => {
    const entry = library.find((e) => e.name === name);
    if (entry) void tableAssets.updateMap(entry);
  };
  return (
    <>
      <button type="button" className={common.btnSmall} data-testid="library-pick-map" onClick={() => setOpen(true)}>
        From my library…
      </button>
      {stale.length > 0 && (
        <ActionSelect
          label="Update a copy from my library"
          testId="build-library-update"
          placeholder="Update…"
          onChoose={update}
        >
          {stale.map((e) => (
            <option key={e.name} value={e.name}>
              {e.name}: v{e.origin.version} → v{tableAssets.lib.assets.find((r) => r.id === e.origin.assetId)?.version}
            </option>
          ))}
        </ActionSelect>
      )}
      {open && (
        <LibraryPicker
          lib={tableAssets.lib}
          uid={tableAssets.uid}
          kind="map"
          title="From my library · Maps"
          action="Copy into the session"
          copies={library}
          onClose={() => setOpen(false)}
          onPick={(row) => {
            setOpen(false);
            void tableAssets.pickMaps([row]);
          }}
          many={{
            label: (n) => (n === 1 ? 'Copy into the session and load it' : `Copy ${n || ''} into the session`),
            onPick: (rows) => {
              setOpen(false);
              void tableAssets.pickMaps(rows);
            },
          }}
        />
      )}
    </>
  );
}

export default function BattlefieldMapAdmin(props) {
  const { building = false, onLoad } = props;
  const library = props.library ?? [];
  const ui = useMapAdminState();
  const pick = (value) => {
    if (value.startsWith(SAVED)) {
      const entry = library.find((e) => e.name === value.slice(SAVED.length));
      if (entry) onLoad?.(entry.terrain);
      return;
    }
    const next = buildPreset(value);
    if (next) onLoad?.(next);
  };
  return (
    <>
      <LoadMap library={library} onPick={pick} rows={props.tableAssets?.lib.assets} />
      {props.tableAssets && <FromLibrary tableAssets={props.tableAssets} library={library} />}
      {building && <Authoring p={{ ...props, library }} ui={ui} />}
    </>
  );
}
